import React from 'react';
import { observer } from 'mobx-react-lite';
import { tokens } from '@fluentui/react-components';

import { useStore } from 'src/hooks/useStore';
import ActivityProgressBox from 'src/components/molecules/activity-progress-box/ActivityProgressBox';
import NavigationTabPanel from '../../molecules/navigation-tab-panel/NavigationTabPanel';
import NavigationTabPanelHeader from '../../molecules/navigation-tab-panel-header/NavigationTabPanelHeader';
import { dailyHabitsTodayStatusStyles } from './DailyHabitsTodayStatus.styles';

interface DailyHabitsTodayStatusProps {
  missedActivities: number
  todayRemainingActivities: number
  todayTotalActivities: number
  upcomingActivities: number
}

export const PROGRESS_COLOR = {
  GREEN: tokens.colorPaletteLightGreenForeground3,
  RED: tokens.colorPaletteRedForeground1,
  ORANGE: tokens.colorPaletteMarigoldBackground3,
};

const DailyHabitsTodayStatus: React.FC<DailyHabitsTodayStatusProps> = (props) => {
  const styles = dailyHabitsTodayStatusStyles();
  const { homeWidgetTextStore } = useStore();
  const { missedActivities, todayRemainingActivities, todayTotalActivities, upcomingActivities } = props;
  const missedCircleColor = missedActivities === 0 ? PROGRESS_COLOR.GREEN : PROGRESS_COLOR.RED;
  const todayCircleColor = (todayTotalActivities === 0 || (todayRemainingActivities / todayTotalActivities) === 1) ? PROGRESS_COLOR.GREEN : undefined;
  const upcomingCircleColor = upcomingActivities === 0 ? PROGRESS_COLOR.GREEN : PROGRESS_COLOR.ORANGE;
  const todayCircleProgress = todayTotalActivities === 0 ? 0 : Math.round(todayRemainingActivities / todayTotalActivities * 100);
  const labelText = {
    todayStatus: homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:Menu:TodayStatus'),
    missedActivityLabel: homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:TodayStatus:MissedActivityLabel'),
    todayActivityLabel: homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:TodayStatus:TodayActivityLabel'),
    upcomingActivityLabel: homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:TodayStatus:UpcomingActivityLabel'),
  };

  const header = <NavigationTabPanelHeader text={labelText.todayStatus} />;

  const body = (
    <div className={styles.contentContainer}>
      <div className={styles.contentWrapper}>
        <ActivityProgressBox
          circleClassName={styles.missedCircle}
          displayedValue={`${missedActivities}`}
          label={labelText.missedActivityLabel}
          progress={100}
          progressColor={missedCircleColor}
          testId="daily-habits-missed-circle-box"
        />

        <ActivityProgressBox
          circleClassName={styles.todayCircle}
          displayedValue={`${todayRemainingActivities}/${todayTotalActivities}`}
          label={labelText.todayActivityLabel}
          progress={todayCircleProgress}
          progressColor={todayCircleColor}
          testId="daily-habits-today-circle-box"
        />

        <ActivityProgressBox
          circleClassName={styles.upcomingCircle}
          displayedValue={`${upcomingActivities}`}
          label={labelText.upcomingActivityLabel}
          progress={100}
          progressColor={upcomingCircleColor}
          testId="daily-habits-upcoming-circle-box"
        />
      </div>
    </div>
  );

  return (
    <div data-testid="daily-habits-today-status">
      <NavigationTabPanel header={header}>
        {body}
      </NavigationTabPanel>
    </div>
  );
};

export default observer(DailyHabitsTodayStatus);
